import {
  getPickedEmployee,
  getRecommendedServices,
} from '@/components/modules/modals/BookAppointment/BookAppointment.helpers';
import { getAnyEmployee } from '../constants';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { BookState } from '@/types/state/book';
import { useMemo } from 'react';
import { SelectAddOnsProps } from './SelectAddOns.types';
import { resolvePriceListId, toSelectServiceItem } from '../serviceItem.helpers';

type SelectAddOnsHandlers = Pick<SelectAddOnsProps, 'onPickService' | 'onShowServiceInfo' | 'onHideServiceInfo'>;
type SelectAddOnsHookResult = Pick<
  SelectAddOnsProps,
  'services' | 'onPickService' | 'onShowServiceInfo' | 'onHideServiceInfo'
> & {
  isNarrow: boolean;
};

/**
 * Derives SelectAddOns' presentational props from BookAppointment Redux state
 * (state.book): the recommended add-ons for the picked main service, mapped to the
 * ServiceCard display shape via the shared toSelectServiceItem (isRecommended = true
 * so rows show the add CTA).
 */
export function useSelectAddOns(handlers: SelectAddOnsHandlers): SelectAddOnsHookResult {
  const { isMobileView } = useMobileView();
  const { place, employee, services } = useAppSelector((s) => s.book) as BookState;
  const pickedEmployee =
    (employee != null ? getPickedEmployee(employee, place?.employees ?? []) : undefined) ?? getAnyEmployee();
  const priceListId = resolvePriceListId(pickedEmployee);
  // Deps are the inputs `pickedEmployee` is derived from, not `pickedEmployee` itself:
  // `getAnyEmployee()` returns a fresh object per render, so adding it here — as the
  // exhaustive-deps warning suggests — would recompute on every render whenever no
  // employee is picked.
  const recommended = useMemo(
    () => getRecommendedServices(services ?? [], pickedEmployee),
    [services, employee, place],
  );
  const pickedMainServices = (services ?? []).filter((s) => !s?.about?.settings?.isAddOn);
  const canShow = recommended.length > 0 && pickedMainServices.length <= 1;
  const items = canShow ? recommended.map((service) => toSelectServiceItem(service, place, priceListId, true)) : [];
  return { services: items, isNarrow: isMobileView, ...handlers };
}
